import MaTab from '../../common/models/MaTab'
import BasicDetails from '../pages/BasicDetails'
import CarriageCharges from '../pages/CarriageCharges'
import AdvancedSettings from '../pages/AdvancedSettings'
import React, { useEffect, useState } from 'react'
import useSupplier from './useSuppliers'
import {
  type SupplierChangeRequestDto,
  type SupplierIntegrationSettingRequestDto,
  type SupplierDto,
  type SupplierIntegrationSettingDto
} from '@amici/myamici-integrations-client'
import { useToastNotification } from '../../common/components/ToastNotificationContextProvider'
import { useTranslation } from 'react-i18next'
import useCountries from './useCountries'
import {
  type Country,
  type Currency,
  type SupplierType
} from '@amici/myamici-api-client'
import useIsoCountries from './useIsoCountries'
import useCurrencies from './useCurrencies'
import styles from '../assets/scss/Suppliers.module.scss'
import useAdminUsersApi from './useAdminUsersApi'
import useSupplierTypes from './useSupplierTypes'
import useSupplierIntegrationsSettings from './useSupplierIntegrationSettings'
import { type AccountResource } from '@amici/myamici-api-client/models/account-resource'
import MaSwitch from '../../common/components/MaSwitch'

export interface SupplierTabProps {
  supplierData: SupplierDto | undefined
  countries: Country[] | undefined
  isoCountries: Country[] | undefined
  currencies: Currency[] | undefined
  admins: AccountResource[] | undefined
  supplierTypes: SupplierType[] | undefined
  isLoading: boolean
  hasLoadingError: boolean
  onDirtyChange?: (isDirty: boolean) => void
  onSubmit: (supplierData: SupplierChangeRequestDto) => void
}

export interface SupplierSettingsTabProps {
  supplierSettingsData: SupplierIntegrationSettingDto | undefined
  isLoading: boolean
  hasLoadingError: boolean
  onDirtyChange?: (isDirty: boolean) => void
  onSubmit: (supplierSettingsData: SupplierIntegrationSettingRequestDto) => void
}

export interface SupplierSettingsTabState {
  supplierName: string | null | undefined
  tabs: MaTab[]
  isLoading: boolean
  isFormDisabled: boolean
  isDirty: boolean
}

function useSupplierTabs (supplierId?: string): SupplierSettingsTabState {
  const { showToastMessage } = useToastNotification()
  const { t } = useTranslation()

  const { data, isLoading: isSupplierLoading, update } = useSupplier(supplierId)

  const {
    data: ssData,
    isLoading: isSupplierSettingsLoading,
    update: supplierSettingsUpdate
  } = useSupplierIntegrationsSettings(supplierId)

  const {
    countries,
    isLoading: isCountriesLoading,
    loadingError: countriesLoadingError
  } = useCountries()
  const {
    isoCountries,
    isLoading: isIsoCountriesLoading,
    loadingError: ISOCountriesLoadingError
  } = useIsoCountries()
  const {
    currencies,
    isLoading: isCurrenciesLoading,
    loadingError: currenciesLoadingError
  } = useCurrencies()
  const {
    admins,
    isLoading: isAdminsLoading,
    loadingError: adminsLoadingError
  } = useAdminUsersApi()
  const {
    supplierTypes,
    isLoading: isSupplierTypesLoading,
    loadingError: supplierTypesLoadingError
  } = useSupplierTypes()

  const hasLoadingError = !!(
    countriesLoadingError ||
    ISOCountriesLoadingError ||
    currenciesLoadingError ||
    adminsLoadingError ||
    supplierTypesLoadingError
  )
  const isLoading =
    isSupplierLoading ||
    isCountriesLoading ||
    isIsoCountriesLoading ||
    isCurrenciesLoading ||
    isAdminsLoading ||
    isSupplierTypesLoading

  const [isBasicDetailsDirty, setBasicDetailsDirty] = useState(false)
  const [isCarriageChargesDirty, setCarriageChargesDirty] = useState(false)
  const [isAdvancedSettingsDirty, setAdvancedSettingsDirty] = useState(false)

  const isAnyFormDirty =
    isBasicDetailsDirty || isCarriageChargesDirty || isAdvancedSettingsDirty

  const [isDisabled, setIsDisabled] = useState(false)
  const [supplierData, setSupplierData] = useState<SupplierDto | undefined>(
    data
  )
  const [supplierSettingsData, setSupplierSettingsData] = useState<
    SupplierIntegrationSettingDto | undefined
  >(ssData)

  // wait for the initial data
  useEffect(() => {
    if (data) {
      setSupplierData(data)
      setIsDisabled(!data.active)
    }
  }, [data])

  useEffect(() => {
    if (ssData) {
      setSupplierSettingsData(ssData)
    }
  }, [ssData])

  function onSubmit (formData: SupplierChangeRequestDto): void {
    update(formData)
      .then(result => {
        setSupplierData(result)
        showToastMessage('dark', t('suppliers.save.success'))
      })
      .catch(() => {
        showToastMessage('danger', t('suppliers.save.error'))
      })
  }

  function onSubmitSettings (
    supplierSettingsToUpdate: SupplierIntegrationSettingRequestDto
  ): void {
    supplierSettingsUpdate(supplierSettingsToUpdate)
      .then(result => {
        setSupplierSettingsData(result)
        showToastMessage('dark', t('suppliers.save.success'))
      })
      .catch(() => {
        showToastMessage('danger', t('suppliers.save.error'))
      })
  }

  function handleActive (checked: boolean): boolean {
    if (data) {
      data.active = checked
      update(data)
        .then(result => {
          setSupplierData(result)
          showToastMessage('dark', t('suppliers.save.success'))
        })
        .catch(() => {
          showToastMessage('danger', t('suppliers.save.error'))
        })
    }

    setIsDisabled(!checked)

    return !checked
  }

  const tabs = [
    new MaTab(
      'basicDetails',
      (
        <BasicDetails
          supplierData={supplierData}
          countries={countries}
          isoCountries={isoCountries}
          currencies={currencies}
          admins={admins}
          supplierTypes={supplierTypes}
          isLoading={isLoading}
          hasLoadingError={hasLoadingError}
          onDirtyChange={setBasicDetailsDirty}
          onSubmit={onSubmit}
        />
      )
    ),
    new MaTab(
      'carriageCharges',
      (
        <CarriageCharges
          supplierData={supplierData}
          countries={countries}
          isoCountries={isoCountries}
          currencies={currencies}
          admins={admins}
          supplierTypes={supplierTypes}
          isLoading={isLoading}
          hasLoadingError={hasLoadingError}
          onDirtyChange={setCarriageChargesDirty}
          onSubmit={onSubmit}
        />
      )
    ),
    new MaTab(
      'advancedSettings',
      (
        <AdvancedSettings
          supplierSettingsData={supplierSettingsData}
          isLoading={isSupplierSettingsLoading}
          hasLoadingError={hasLoadingError}
          onDirtyChange={setAdvancedSettingsDirty}
          onSubmit={onSubmitSettings}
        />
      )
    ),
    new MaTab(
      'activeButton',
      (
        <div className={styles['field-status']}>
          <MaSwitch
            id="field-status"
            checked={data?.active ?? false}
            onCheckedChange={checked => {
              void handleActive(checked)
            }}
          >
            {(data?.active ?? false)
              ? t('common.status.values.active')
              : t('common.status.values.inactive')}
          </MaSwitch>
        </div>
      ),
      true,
      true,
      undefined,
      false,
      true
    )
  ]

  return {
    supplierName: data?.supplierName,
    tabs: tabs.filter(tab => tab.visible),
    isLoading: isSupplierLoading,
    isDirty: isAnyFormDirty,
    isFormDisabled: isDisabled
  }
}

export default useSupplierTabs
